/**
 * Form fields for a sport profile (cricket today; padel / pickleball later).
 * The field list comes from the backend (GET /auth/sport-profiles/schema), so
 * a new sport needs no new form code here.
 */
import { useEffect, useState } from 'react';
import { axiosInstance } from '../../config/axios';

let schemaCache = null;

export function useSportSchema(sport) {
  const [spec, setSpec] = useState(() => schemaCache?.find((s) => s.sport === sport) || null);
  useEffect(() => {
    let alive = true;
    const pick = (all) => alive && setSpec(all.find((s) => s.sport === sport) || null);
    if (schemaCache) { pick(schemaCache); return () => { alive = false; }; }
    axiosInstance.get('/auth/sport-profiles/schema')
      .then((res) => { schemaCache = res.data || []; pick(schemaCache); })
      .catch(() => {});
    return () => { alive = false; };
  }, [sport]);
  return spec;
}

const isHidden = (f, value) => !!(f.shown_unless && value[f.shown_unless[0]] === f.shown_unless[1]);
const isRequired = (f, value) =>
  f.required && !(f.required_unless && value[f.required_unless[0]] === f.required_unless[1]);

// Labels of required fields still empty (for client-side checks before submit).
export function missingSportFields(spec, value) {
  if (!spec) return [];
  return spec.fields.filter((f) => !isHidden(f, value) && isRequired(f, value) && !value[f.key]).map((f) => f.label);
}

export default function SportProfileFields({ sport, value, onChange, inputCls, Field }) {
  const spec = useSportSchema(sport);
  if (!spec) return <div className="h-24 animate-pulse bg-gray-50 rounded-xl" />;

  const set = (key, v) => {
    const next = { ...value, [key]: v };
    // Clear fields that this change hides (e.g. bowling details for a batter)
    spec.fields.forEach((f) => { if (isHidden(f, next)) delete next[f.key]; });
    onChange(next);
  };

  return (
    <div className="space-y-4" data-testid={`sport-fields-${sport}`}>
      {spec.fields.filter((f) => !isHidden(f, value)).map((f) => (
        <Field key={f.key} label={f.label} required={isRequired(f, value)}>
          {f.kind === 'select' ? (
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={f.label}>
              {f.options.map((opt) => (
                <button type="button" key={opt} role="radio" aria-checked={value[f.key] === opt}
                  onClick={() => set(f.key, value[f.key] === opt && !isRequired(f, value) ? '' : opt)}
                  className={`px-3 py-2 rounded-xl text-sm font-semibold border-2 transition-colors ${
                    value[f.key] === opt ? 'border-[#2B3A67] bg-[#2B3A67] text-white' : 'border-gray-100 text-gray-600 hover:border-gray-200'
                  }`}
                  data-testid={`sport-${f.key}-${opt.replace(/\s+/g, '-').toLowerCase()}`}>
                  {opt}
                </button>
              ))}
            </div>
          ) : (
            <input className={inputCls} type={f.kind === 'url' ? 'url' : 'text'} value={value[f.key] || ''}
              onChange={(e) => set(f.key, e.target.value)}
              placeholder={f.kind === 'url' ? 'https://…' : ''} data-testid={`sport-${f.key}`} />
          )}
          {f.help && <p className="text-xs text-gray-400 mt-1">{f.help}</p>}
        </Field>
      ))}
    </div>
  );
}
